import { z } from 'zod'

/**
 * Review synthesis — turning a review corpus into the judgments Google will
 * never render.
 *
 * The whole competitive thesis is that a page saying "these three roofers
 * consistently show up after storms, and these two have repeated no-show
 * complaints" is worth citing, while a page restating a star rating is not.
 * That only holds if the claims are true.
 *
 * So grounding is enforced structurally, not requested politely: every claim
 * must cite the indexes of the reviews supporting it, and validation drops any
 * claim whose citations are missing, out of range, or too few. A model that
 * writes plausible slurry produces claims that fail validation rather than
 * claims that ship.
 */

/** A claim must be supported by at least this many distinct reviews. */
export const MIN_CITATIONS_PER_CLAIM = 2

/** Below this we do not have enough signal to say anything defensible. */
export const MIN_REVIEWS_TO_SYNTHESIZE = 5

/** Reviews sampled per synthesis call. */
export const MAX_REVIEWS_IN_PROMPT = 60

/** Individual review text is truncated to this many characters. */
export const MAX_REVIEW_CHARS = 600

/** Published bounds. Enforced in validateGrounding(), not during generation. */
export const MAX_PROS = 5
export const MAX_WATCH_OUTS = 3
export const MAX_THEMES = 12
export const MAX_CLAIM_CHARS = 400
export const MAX_BEST_FOR_CHARS = 120
export const MAX_THEME_CHARS = 60
export const MAX_WHY_IT_RANKS_CHARS = 900

/**
 * Slack allowed on the -1..1 sentiment scale before we stop believing the
 * number entirely.
 *
 * A model working on the right scale misses by a rounding artifact (1.02). A
 * model that has drifted onto stars or percent misses by a mile (4.5, 85), and
 * clipping that to 1 would hand the ranking a saturated signal that looks
 * exactly like a genuinely glowing corpus. Inside the tolerance we clamp;
 * outside it the score is unknown.
 */
export const SENTIMENT_CLAMP_TOLERANCE = 0.05

/*
 * Generation is permissive; validation is strict.
 *
 * This schema carries SHAPE ONLY: no lengths, no counts, no ranges. Every such
 * bound belongs in validateGrounding() instead, and the reason is that a bound
 * here is not a constraint on the model, it is a constraint on us.
 *
 * OpenAI's structured outputs does not honour length or count keywords, so a
 * `.max()` written here never reaches the model. It is checked only by the SDK's
 * Zod parse AFTER the response arrives, and a failure there throws
 * NoObjectGeneratedError: the tokens are spent, and the entire synthesis is
 * discarded because one paragraph ran long. That is the worst of both worlds,
 * an unenforced request that can still cost us everything.
 *
 * We learned this once with the citation minimum, which hard-failed the whole
 * call whenever the model produced a single-citation claim, and then again with
 * the length caps. The rule that came out of it: anything the model can get
 * wrong is dropped or clamped per field downstream, so a bad paragraph costs us
 * the paragraph and a bad claim costs us the claim. The prompt still states the
 * real requirements.
 */
const ClaimSchema = z.object({
	text: z.string().describe('The claim, in neutral directory voice.'),
	citations: z
		.array(z.number())
		.describe(
			`Indexes of reviews supporting this claim. At least ${MIN_CITATIONS_PER_CLAIM} distinct reviews are required for it to be published.`,
		),
})

export const ReviewSynthesisSchema = z.object({
	pros: z.array(ClaimSchema),
	watchOuts: z.array(ClaimSchema),
	bestFor: z
		.string()
		.describe('Short "best for" tag, e.g. "Storm damage, fast turnaround".'),
	bestForCitations: z.array(z.number()),
	themes: z
		.array(z.string())
		.describe('Recurring topics across reviews, lowercase noun phrases.'),
	sentimentScore: z.number().describe('Aggregate sentiment across the corpus.'),
	whyItRanks: z.string().describe('Paragraph for the /best/ ranking page.'),
})

export type ReviewSynthesis = z.infer<typeof ReviewSynthesisSchema>
export type SynthesisClaim = z.infer<typeof ClaimSchema>

/*
 * ---------------------------------------------------------------------------
 * Salvage
 *
 * The shape-only schema removed the bounds that were failing the Zod parse, but
 * it cannot remove the parse. Whatever is left that the SDK can still reject,
 * truncated JSON when the model runs out of output tokens, a citation emitted
 * as "3" instead of 3, a missing top-level key, throws
 * NoObjectGeneratedError AFTER the tokens are billed.
 *
 * That is the same trade the bounds made, and it gets the same answer: a
 * malformed field should cost us the field, not the synthesis. The error
 * carries the raw text, so the response is recoverable; these functions repair
 * what can be repaired and hand the result to validateGrounding, which already
 * drops anything that does not hold up.
 *
 * Nothing here invents content. It fixes representations (string number to
 * number, truncated array to the elements that arrived) and drops what it
 * cannot read. An unreadable sentiment becomes NaN rather than 0, so
 * resolveSentiment records it as unknown instead of publishing "mixed".
 * ---------------------------------------------------------------------------
 */

/** Characters of raw output we will attempt to repair. Beyond this it is noise. */
const MAX_SALVAGE_CHARS = 200_000

/** Truncation points tried when the tail of the JSON is incomplete. */
const MAX_TRUNCATION_RETRIES = 8

/**
 * Closes the containers and string a truncated response left open, so the
 * elements that DID arrive can be parsed. Returns null when the text ends
 * somewhere no closing sequence can rescue.
 */
function closeOpenStructures(text: string): string | null {
	const stack: string[] = []
	let inString = false
	let escaped = false

	for (const char of text) {
		if (escaped) {
			escaped = false
			continue
		}
		if (inString) {
			if (char === '\\') escaped = true
			else if (char === '"') inString = false
			continue
		}
		if (char === '"') inString = true
		else if (char === '{' || char === '[') stack.push(char)
		else if (char === '}' || char === ']') stack.pop()
	}

	// A trailing backslash would escape the quote we are about to add.
	let repaired = escaped ? text.slice(0, -1) : text
	if (inString) repaired += '"'

	repaired = repaired.trimEnd()
	// A dangling separator is not valid JSON: drop the comma, give the orphaned
	// key a value.
	if (repaired.endsWith(',')) repaired = repaired.slice(0, -1)
	else if (repaired.endsWith(':')) repaired += 'null'

	if (stack.length === 0) return repaired === text ? null : repaired

	const closers = stack
		.reverse()
		.map((open) => (open === '{' ? '}' : ']'))
		.join('')
	return repaired + closers
}

/**
 * The first complete `{...}` in the text, string-aware so a brace inside a
 * review quote does not end the object early. Null when it never closes.
 */
function firstBalancedObject(text: string, start: number): string | null {
	let depth = 0
	let inString = false
	let escaped = false

	for (let i = start; i < text.length; i++) {
		const char = text[i]
		if (escaped) {
			escaped = false
			continue
		}
		if (inString) {
			if (char === '\\') escaped = true
			else if (char === '"') inString = false
			continue
		}
		if (char === '"') inString = true
		else if (char === '{') depth++
		else if (char === '}' && --depth === 0) return text.slice(start, i + 1)
	}

	return null
}

/**
 * Parses model output that is not quite JSON: fenced in markdown, prefixed with
 * prose, trailed by a sign-off, or cut off mid-object by the output token limit.
 */
export function parseLooseJson(raw: string): unknown {
	const text = raw.slice(0, MAX_SALVAGE_CHARS).replace(/```(?:json)?/gi, '')
	const start = text.indexOf('{')
	if (start === -1) return null

	const body = text.slice(start).trimEnd()
	try {
		return JSON.parse(body)
	} catch {
		// Not valid as-is; fall through to repair.
	}

	/*
	 * A complete object followed by prose ("Hope this helps!") is the common
	 * case, and it is not truncated at all. Cutting it back to the last comma
	 * would silently discard the final field, so take the balanced slice first
	 * and only treat the response as truncated if there isn't one.
	 */
	const balanced = firstBalancedObject(body, 0)
	if (balanced && balanced !== body) {
		try {
			return JSON.parse(balanced)
		} catch {
			// The object closes but its contents are malformed; repair below.
		}
	}

	/*
	 * Try the whole body first, then walk backwards element by element. The last
	 * element of a truncated response is usually a half-written value that no
	 * amount of bracket-closing makes valid, and dropping it recovers everything
	 * before it.
	 */
	let candidate = body
	for (let attempt = 0; attempt <= MAX_TRUNCATION_RETRIES; attempt++) {
		const repaired = closeOpenStructures(candidate)
		if (repaired) {
			try {
				return JSON.parse(repaired)
			} catch {
				// Still broken further back; keep trimming.
			}
		}

		const lastComma = candidate.lastIndexOf(',')
		if (lastComma <= 0) break
		candidate = candidate.slice(0, lastComma)
	}

	return null
}

function asRecord(value: unknown): Record<string, unknown> | null {
	return typeof value === 'object' && value !== null && !Array.isArray(value)
		? (value as Record<string, unknown>)
		: null
}

function asString(value: unknown): string {
	return typeof value === 'string' ? value : ''
}

/**
 * Citations as numbers. A model that emits "3" means index 3.
 *
 * Only a non-empty numeric string is converted. Number('') and Number(' ') are
 * both 0, so a blank entry would otherwise become a citation to review 0: an
 * invented one, and the cheapest possible way to fake grounding, since index 0
 * always exists. NaN is excluded for the same reason typeof is not enough to
 * screen it. Non-integers survive to validateGrounding, which drops them.
 */
function coerceCitations(value: unknown): number[] {
	if (!Array.isArray(value)) return []
	return value
		.map((entry) => {
			if (typeof entry === 'number') return entry
			if (typeof entry !== 'string') return Number.NaN
			const trimmed = entry.trim()
			return trimmed ? Number(trimmed) : Number.NaN
		})
		.filter((entry) => Number.isFinite(entry))
}

function coerceClaims(value: unknown): SynthesisClaim[] {
	if (!Array.isArray(value)) return []
	return value.flatMap((entry) => {
		const record = asRecord(entry)
		if (!record) return []
		const text = asString(record.text).trim()
		if (!text) return []
		return [{ text, citations: coerceCitations(record.citations) }]
	})
}

/**
 * Rebuilds a ReviewSynthesis from output the SDK's Zod parse rejected.
 *
 * Returns null when there are no claims to publish: an empty shell would pass
 * through validateGrounding as "nothing survived", overwrite the business's
 * existing editorial with blanks, and report success. Failing the task instead
 * leaves the previous synthesis in place and lets the retry run.
 */
export function salvageSynthesis(
	raw: string | undefined,
): ReviewSynthesis | null {
	if (!raw?.trim()) return null

	const parsed = asRecord(parseLooseJson(raw))
	if (!parsed) return null

	const pros = coerceClaims(parsed.pros)
	const watchOuts = coerceClaims(parsed.watchOuts)
	if (pros.length === 0 && watchOuts.length === 0) return null

	const sentiment = parsed.sentimentScore
	const sentimentScore =
		typeof sentiment === 'number'
			? sentiment
			: typeof sentiment === 'string' && sentiment.trim()
				? Number(sentiment)
				: // Not a reading. resolveSentiment turns non-finite into unknown,
					// which is what an unparseable score is.
					Number.NaN

	return {
		pros,
		watchOuts,
		bestFor: asString(parsed.bestFor).trim(),
		bestForCitations: coerceCitations(parsed.bestForCitations),
		themes: Array.isArray(parsed.themes)
			? parsed.themes.filter((t): t is string => typeof t === 'string')
			: [],
		sentimentScore,
		whyItRanks: asString(parsed.whyItRanks).trim(),
	}
}

/** Top-level fields a complete response carries, in schema order. */
const SYNTHESIS_FIELDS = Object.keys(ReviewSynthesisSchema.shape)

/**
 * True when the raw output stopped before the whole object arrived.
 *
 * This is the difference between "one field is malformed" and "the response was
 * cut off", and only the first is safe to salvage and publish. Field order puts
 * pros first and themes, sentiment and whyItRanks last, so a response truncated
 * by the output token limit salvages into a shell: a pro or two, and silence
 * everywhere else. Published, that shell reads as a business we found nothing
 * to say about.
 *
 * Absence of a top-level key is the signal, because a complete response always
 * carries all of them; structured outputs makes every field required, and
 * salvage only ever adds keys the model itself wrote.
 */
export function isPartialSynthesisOutput(raw: string | undefined): boolean {
	const parsed = asRecord(parseLooseJson(raw ?? ''))
	if (!parsed) return true
	return SYNTHESIS_FIELDS.some((field) => !(field in parsed))
}

export const REVIEW_SYNTHESIS_SYSTEM_PROMPT = `
You analyze customer reviews for a local business directory covering the
Dallas–Fort Worth metro. Your output is read by people choosing who to hire for
work that costs real money.

GROUNDING RULES (hard requirements, not style preferences)
- Every claim MUST cite the indexes of the reviews that support it.
- A claim needs support from at least ${MIN_CITATIONS_PER_CLAIM} DIFFERENT reviews. One
  reviewer's opinion is an anecdote, not a pattern.
- Never state anything the reviews do not say. No inferred pricing, no assumed
  certifications, no invented service areas, no guessed years in business.
- If the corpus does not support a claim, omit it. Returning two well-supported
  pros is correct. Padding to ${MAX_PROS} is failure.
- Watch-outs must come from actual complaints. Do not manufacture balance.
- NEVER write "one reviewer", "a customer", or any singular attribution. Every
  published claim describes a pattern across two or more reviews, so singular
  phrasing contradicts what the page tells readers. Write "several reviewers",
  "multiple customers", or name the pattern directly.

VOICE
- Neutral directory voice. Specific and concrete.
- Describe observed patterns: "several reviewers describe crews arriving within
  a day of a storm call" rather than "excellent emergency response."
- Ban: best, premier, top-rated, world-class, unparalleled, one-stop,
  award-winning, hidden gem, second to none, exceptional, outstanding.
- No exclamation points. No second person. No sales language.
- Never use em dashes or en dashes. Use commas, colons, or separate sentences.
- Every sentence must help someone decide whether to hire this business. Cut
  anything that only fills space.

LENGTH BUDGETS (anything over these is dropped, not trimmed)
- At most ${MAX_PROS} pros and ${MAX_WATCH_OUTS} watch-outs, each under ${MAX_CLAIM_CHARS} characters.
- bestFor: under ${MAX_BEST_FOR_CHARS} characters. It is a tag, not a sentence.
- whyItRanks: under ${MAX_WHY_IT_RANKS_CHARS} characters.
- At most ${MAX_THEMES} themes, each under ${MAX_THEME_CHARS} characters.

THEMES
- Lowercase noun phrases naming what reviewers actually discuss:
  "cleanup after tear-off", "insurance claim paperwork", "callback delays".
- Not sentiment words. "quality" and "professionalism" are useless themes.

SENTIMENT
- sentimentScore is a decimal from -1 to 1. -1 is uniformly negative, 0 is
  mixed or neutral, 1 is uniformly positive. It is NOT a star rating and NOT a
  percentage: 4.5 and 85 are both wrong, whatever the reviews are rated.
- Weigh the balance of positive and negative language across the whole corpus,
  not the star average. A 4.6 with recurring scheduling complaints is not 0.9.

WHY IT RANKS
- One paragraph for the ranking page. Lead with what distinguishes this
  business, grounded in the review patterns. Name the trade-off if one exists.
`.trim()

export interface PreparedReview {
	index: number
	/** Review id, kept so citations can be resolved back to rows. */
	id: string
	rating: number | null
	text: string
	timestamp: string | null
}

/**
 * Selects and formats the reviews shown to the model.
 *
 * Sampling is deliberately spread across ratings rather than taking the most
 * recent N: a corpus of 200 five-star reviews and 8 one-stars would otherwise
 * hide every complaint, and the watch-outs are the part competitors cannot
 * copy from Google.
 */
export function prepareReviewsForPrompt(
	reviews: Array<{
		id: string
		rating: number | null
		text: string | null
		timestamp: Date | null
	}>,
	limit = MAX_REVIEWS_IN_PROMPT,
): PreparedReview[] {
	const withText = reviews.filter((r): r is typeof r & { text: string } =>
		Boolean(r.text?.trim()),
	)

	const negative = withText.filter((r) => (r.rating ?? 5) <= 3)
	const positive = withText.filter((r) => (r.rating ?? 5) > 3)

	const byRecency = <T extends { timestamp: Date | null }>(a: T, b: T) =>
		(b.timestamp?.getTime() ?? 0) - (a.timestamp?.getTime() ?? 0)

	negative.sort(byRecency)
	positive.sort(byRecency)

	// Guarantee negatives a share of the window so complaints cannot be
	// crowded out, but never pad beyond what exists.
	const negativeQuota = Math.min(negative.length, Math.floor(limit * 0.4))
	const selected = [
		...negative.slice(0, negativeQuota),
		...positive.slice(0, limit - negativeQuota),
	]

	return selected.map((review, index) => ({
		index,
		id: review.id,
		rating: review.rating,
		text: review.text.trim().slice(0, MAX_REVIEW_CHARS),
		timestamp: review.timestamp?.toISOString().slice(0, 10) ?? null,
	}))
}

/**
 * Halves of a surrogate pair with no partner.
 *
 * MAX_REVIEW_CHARS counts UTF-16 code units, so truncation can land between the
 * two halves of an emoji and orphan one of them. An orphan serialises into the
 * request body as an unpaired \ud83d escape, which is not valid JSON: the API
 * rejects the whole request before reading a word of the prompt, and every retry
 * rebuilds the same bytes and fails identically. Scraped business names and city
 * names can carry the same damage without any truncation on our side.
 *
 * Dropping the orphan costs nothing — the character it belonged to was already
 * destroyed by the cut that stranded it.
 */
const LONE_SURROGATE =
	/[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g

/**
 * Strips surrogate halves that no longer have a partner, so a broken character
 * cannot cost us the request. Applied to the assembled prompt rather than to
 * each field, so it covers everything we send however it got there.
 */
function stripLoneSurrogates(text: string): string {
	return text.replace(LONE_SURROGATE, '')
}

export function buildSynthesisUserPrompt(input: {
	businessName: string
	category: string
	city: string
	reviews: PreparedReview[]
}): string {
	const corpus = input.reviews
		.map(
			(r) =>
				`[${r.index}] ${r.rating != null ? `${r.rating}★` : 'no rating'}${
					r.timestamp ? ` ${r.timestamp}` : ''
				}\n${r.text}`,
		)
		.join('\n\n')

	return stripLoneSurrogates(
		[
			`Business: ${input.businessName}`,
			`Category: ${input.category}`,
			`City: ${input.city}`,
			`Reviews held: ${input.reviews.length}`,
			'',
			'REVIEWS (cite by index):',
			corpus,
		].join('\n'),
	)
}

/**
 * Marketing vocabulary that makes copy read as machine-generated. The system
 * prompt bans these; this list is also applied to the OUTPUT, because a prompt
 * instruction is a request and a filter is a guarantee.
 */
export const BANNED_OUTPUT_TERMS = [
	'best-in-class',
	'top-rated',
	'top rated',
	'premier',
	'world-class',
	'world class',
	'award-winning',
	'award winning',
	'hidden gem',
	'second to none',
	'one-stop',
	'one stop shop',
	'unparalleled',
	'exceptional',
	'outstanding',
	'exemplary',
	'cutting-edge',
	'state-of-the-art',
	'go-to choice',
	'peace of mind',
]

const BANNED_PATTERN = new RegExp(
	`\\b(${BANNED_OUTPUT_TERMS.map((t) => t.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')).join('|')})\\b`,
	'i',
)

/** True when text contains marketing vocabulary we refuse to publish. */
export function containsBannedTerms(text: string): boolean {
	return BANNED_PATTERN.test(text)
}

/** The banned terms present in a string, for logging what was rejected. */
export function findBannedTerms(text: string): string[] {
	const lower = text.toLowerCase()
	return BANNED_OUTPUT_TERMS.filter((term) => {
		const pattern = new RegExp(
			`\\b${term.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}\\b`,
			'i',
		)
		return pattern.test(lower)
	})
}

/**
 * Phrasing that attributes a claim to a single person. Published claims are
 * always patterns across two or more reviews.
 */
/** Em and en dashes are not used in DFW Professionals copy. */
const DASH_PATTERN = /[\u2013\u2014]/

const SINGULAR_ATTRIBUTION = new RegExp(
	[
		// Explicit singular counts: "one reviewer", "a single customer".
		String.raw`\b(?:one|a single)\s+(?:reviewer|customer|client|homeowner|person)\b`,
		// Indefinite article only when followed by a reporting verb, so
		// "a customer service desk" and "a homeowner-friendly schedule" survive
		// while "a reviewer noted" does not.
		String.raw`\b(?:a|an)\s+(?:reviewer|customer|client|homeowner|person)\s+(?:noted|note|said|says|mentioned|mentions|reported|reports|described|describes|complained|complains|wrote|writes|experienced|experiences|recalled|recalls|claimed|claims)\b`,
	].join('|'),
	'i',
)

/**
 * House-style check applied to every piece of published copy, not just the
 * cited claims. Returns a rejection reason, or null when the text is clean.
 *
 * whyItRanks and bestFor go through this too: the ranking paragraph is the most
 * visible copy on the site, and a prompt instruction alone is a request rather
 * than a guarantee.
 */
function styleViolation(text: string, maxChars: number): string | null {
	if (!text.trim()) return 'empty'

	/*
	 * Over-length copy is dropped rather than truncated. A ranking paragraph cut
	 * mid-sentence reads worse than no paragraph, and a claim cut mid-clause can
	 * invert its meaning.
	 */
	if (text.length > maxChars) {
		return `${text.length} characters, over the ${maxChars} limit`
	}

	if (DASH_PATTERN.test(text)) return 'contains an em or en dash'

	/*
	 * Singular attribution contradicts the page's own promise. Every published
	 * claim is backed by two or more reviews, so "one reviewer noted..." tells
	 * the reader something we explicitly say is not true.
	 */
	if (SINGULAR_ATTRIBUTION.test(text)) {
		return 'singular attribution contradicts the multi-review requirement'
	}

	const banned = findBannedTerms(text)
	if (banned.length > 0) {
		return `banned marketing terms: ${banned.join(', ')}`
	}

	return null
}

/**
 * What survived validation. Differs from ReviewSynthesis in one place:
 * sentimentScore can come back null, meaning "the model gave us no usable
 * reading", which is distinct from the 0 that means "mixed". Business
 * .sentimentScore is nullable and sentimentFactor() already resolves null to
 * UNKNOWN_FACTOR_VALUE, so unknown travels the whole way without inventing a
 * ranking signal.
 */
export interface ValidatedSynthesis
	extends Omit<ReviewSynthesis, 'sentimentScore'> {
	sentimentScore: number | null
}

export interface ValidationResult {
	synthesis: ValidatedSynthesis
	/**
	 * Output removed because its citations, vocabulary, length or scale did not
	 * hold up. `text` is truncated for the log, so it identifies what was dropped
	 * rather than reproducing it.
	 */
	rejected: Array<{ field: string; text: string; reason: string }>
	/**
	 * False when too little survived validation to publish editorial. The
	 * ranking paragraph is the most visible copy on the site, so it must not
	 * ship on the back of claims that were all rejected.
	 */
	isPublishable: boolean
}

/**
 * Resolves the raw sentiment reading, recording anything we had to change.
 *
 * Sentiment is the one field with no citations to check, so a wrong value has
 * nothing to fail against: it flows straight into sentimentFactor() and moves
 * the business's rank. Clipping a 4.5 to 1 would read downstream as a corpus
 * with no complaints in it, which is a claim we would never let a *sentence*
 * make without two reviews behind it.
 *
 * So: rounding slack is clamped, real scale drift is unknown, and both land in
 * `rejected` so the run log says which happened.
 */
function resolveSentiment(
	raw: number,
	rejected: ValidationResult['rejected'],
): number | null {
	if (!Number.isFinite(raw)) {
		rejected.push({
			field: 'sentimentScore',
			text: String(raw),
			reason: 'not a finite number, treated as unknown',
		})
		return null
	}

	if (raw >= -1 && raw <= 1) return raw

	if (Math.abs(raw) <= 1 + SENTIMENT_CLAMP_TOLERANCE) {
		const clamped = Math.max(-1, Math.min(1, raw))
		rejected.push({
			field: 'sentimentScore',
			text: String(raw),
			reason: `outside -1..1 by rounding, clamped to ${clamped}`,
		})
		return clamped
	}

	rejected.push({
		field: 'sentimentScore',
		text: String(raw),
		reason:
			'far outside -1..1, likely a star or percent scale, treated as unknown',
	})
	return null
}

/** Rejected text is truncated to this in the log. Enough to identify it. */
const REJECTED_TEXT_LOG_CHARS = 120

/**
 * Trims text on its way into `rejected`.
 *
 * The reason a value was rejected is usually that it was too long, so the
 * rejection log is where the worst payloads collect. The point of the entry is
 * to identify which output was dropped, and the opening line does that; the
 * remaining few hundred characters only make the Trigger warn harder to read.
 */
function forLog(text: string): string {
	return text.length > REJECTED_TEXT_LOG_CHARS
		? `${text.slice(0, REJECTED_TEXT_LOG_CHARS)}…`
		: text
}

/**
 * Drops any claim whose citations are missing, out of range, duplicated down to
 * fewer than the minimum, or otherwise unverifiable.
 *
 * This is the actual defense against slurry. The prompt asks for grounding;
 * this enforces it.
 */
export function validateGrounding(
	synthesis: ReviewSynthesis,
	reviewCount: number,
): ValidationResult {
	const rejected: ValidationResult['rejected'] = []

	/*
	 * A citation is only usable if it names an actual row we showed the model.
	 * Integer-ness is checked here rather than in the schema for the same reason
	 * as everything else: 2.5 should cost us one citation, not the synthesis.
	 */
	const isCitableIndex = (index: number): boolean =>
		Number.isInteger(index) && index >= 0 && index < reviewCount

	/**
	 * Trims a list to its published cap, recording what was dropped. The model is
	 * asked for the cap and usually respects it; when it does not, the surplus is
	 * the tail it considered least important.
	 */
	const applyCap = <T extends { text: string }>(
		claims: T[],
		cap: number,
		field: string,
	): T[] => {
		for (const surplus of claims.slice(cap)) {
			rejected.push({
				field,
				text: forLog(surplus.text),
				reason: `beyond the ${cap}-claim cap`,
			})
		}
		return claims.slice(0, cap)
	}

	const isValidClaim = (claim: SynthesisClaim, field: string): boolean => {
		const style = styleViolation(claim.text, MAX_CLAIM_CHARS)
		if (style) {
			rejected.push({ field, text: forLog(claim.text), reason: style })
			return false
		}

		const unique = [...new Set(claim.citations)].filter(isCitableIndex)

		if (unique.length < MIN_CITATIONS_PER_CLAIM) {
			rejected.push({
				field,
				text: forLog(claim.text),
				reason:
					unique.length === claim.citations.length
						? `only ${unique.length} supporting review(s), needs ${MIN_CITATIONS_PER_CLAIM}`
						: 'citations out of range or duplicated',
			})
			return false
		}
		return true
	}

	// Caps apply to what survived, so five weak pros cannot crowd out a sixth
	// well-supported one.
	const pros = applyCap(
		synthesis.pros.filter((c) => isValidClaim(c, 'pros')),
		MAX_PROS,
		'pros',
	)
	const watchOuts = applyCap(
		synthesis.watchOuts.filter((c) => isValidClaim(c, 'watchOuts')),
		MAX_WATCH_OUTS,
		'watchOuts',
	)

	const bestForCitations = [...new Set(synthesis.bestForCitations)].filter(
		isCitableIndex,
	)
	const bestForStyle = styleViolation(synthesis.bestFor, MAX_BEST_FOR_CHARS)
	const bestForHolds =
		bestForCitations.length >= MIN_CITATIONS_PER_CLAIM && bestForStyle === null
	if (!bestForHolds) {
		rejected.push({
			field: 'bestFor',
			text: forLog(synthesis.bestFor),
			reason: bestForStyle ?? 'insufficient supporting reviews',
		})
	}

	/*
	 * whyItRanks is the most visible copy we publish — it is the paragraph a
	 * reader (or a citing model) actually sees on /best/. It carries no
	 * citations of its own, so it inherits the credibility of the claims that
	 * survived. If nothing survived, the paragraph is unsupported prose and is
	 * dropped rather than shipped.
	 */
	const whyItRanksStyle = styleViolation(
		synthesis.whyItRanks,
		MAX_WHY_IT_RANKS_CHARS,
	)
	const hasSurvivingClaims = pros.length > 0 || watchOuts.length > 0
	const whyItRanksHolds = hasSurvivingClaims && whyItRanksStyle === null

	if (!whyItRanksHolds) {
		rejected.push({
			field: 'whyItRanks',
			text: forLog(synthesis.whyItRanks),
			reason: whyItRanksStyle ?? 'no supporting claims survived validation',
		})
	}

	// Themes are only meaningful when they describe a corpus we could actually
	// draw claims from, and only useful once normalized. An over-length theme is
	// a sentence wearing a noun phrase's clothes, so it is dropped before
	// normalization rather than allowed to anchor a group.
	//
	// Every drop is recorded. Themes are the one output with no per-item gate of
	// their own, so without this "the themes changed on this run" is invisible in
	// a way a dropped pro never is.
	const themes: string[] = []
	if (hasSurvivingClaims) {
		const usable = synthesis.themes.filter((theme) => {
			if (theme.length <= MAX_THEME_CHARS) return true
			rejected.push({
				field: 'themes',
				text: forLog(theme),
				reason: `${theme.length} characters, over the ${MAX_THEME_CHARS} limit`,
			})
			return false
		})

		const normalized = normalizeThemes(usable)
		themes.push(...normalized.slice(0, MAX_THEMES))
		for (const surplus of normalized.slice(MAX_THEMES)) {
			rejected.push({
				field: 'themes',
				text: forLog(surplus),
				reason: `beyond the ${MAX_THEMES}-theme cap`,
			})
		}
	}

	const sentimentScore = resolveSentiment(synthesis.sentimentScore, rejected)

	return {
		synthesis: {
			...synthesis,
			pros,
			watchOuts,
			themes,
			sentimentScore,
			bestFor: bestForHolds ? synthesis.bestFor : '',
			bestForCitations,
			whyItRanks: whyItRanksHolds ? synthesis.whyItRanks : '',
		},
		rejected,
		isPublishable: hasSurvivingClaims,
	}
}

/**
 * Themes the model reaches for that describe sentiment rather than subject.
 * "communication" is not a topic, it is a verdict; every service business has
 * it. Rejected so themes stay useful for grouping and filtering.
 */
/**
 * Stems that describe a verdict rather than a subject. "communication" is not
 * a topic, it is an opinion, and every service business has one.
 *
 * A theme is dropped only when EVERY content token is vague. Matching bare
 * words anywhere would break other verticals: "service" is noise in "customer
 * service" but load-bearing in "septic service", "junk removal service" and
 * "HVAC service". Requiring all tokens to be vague keeps those.
 */
const VAGUE_STEMS = new Set([
	'communication',
	'communicate',
	'clarity',
	'clear',
	'customer',
	'client',
	'service',
	'servic',
	'professional',
	'professionalism',
	'profession',
	'reliability',
	'reliable',
	'dependable',
	'satisfaction',
	'satisfied',
	'happy',
	'expertise',
	'expert',
	'knowledgeable',
	'knowledge',
	'quality',
	'value',
	'experience',
	'attention',
	'detail',
	'timeliness',
	'timely',
	'efficiency',
	'efficient',
	'courteous',
	'friendly',
	'polite',
	'nice',
	'great',
	'good',
	'overall',
	'general',
	'positive',
	'negative',
	// "work" alone is not a subject in a trades directory, everything is work.
	// "workmanship" is deliberately absent: it names something specific.
	'work',
	'job',
	'project',
	'result',
	'outcome',
])

/**
 * Words carrying no topical meaning, used when comparing themes for overlap.
 */
const THEME_STOPWORDS = new Set([
	'a',
	'an',
	'and',
	'the',
	'of',
	'for',
	'to',
	'in',
	'on',
	'with',
	'at',
	'by',
	'after',
	'during',
	'from',
	'about',
	'issues',
	'problems',
	'process',
	'processes',
	'assistance',
	'support',
	'help',
	'related',
	'general',
])

/** Crude singularization: enough to align "claims" with "claim". */
function stem(token: string): string {
	if (token.length > 4 && token.endsWith('ies')) return `${token.slice(0, -3)}y`
	if (token.length > 3 && token.endsWith('es')) return token.slice(0, -2)
	if (token.length > 3 && token.endsWith('s')) return token.slice(0, -1)
	return token
}

/**
 * Topical tokens only. Verdict-words are excluded alongside stopwords: they
 * carry no subject, so letting them count would both weaken grouping and
 * inflate token counts. "cleanup after job" and "cleanup after work" both
 * reduce to {cleanup} and merge, rather than being held apart by two
 * meaningless tokens.
 */
export function contentTokens(theme: string): Set<string> {
	return new Set(
		theme
			.toLowerCase()
			.split(/[^a-z0-9]+/)
			.filter((token) => token.length > 2 && !THEME_STOPWORDS.has(token))
			.map(stem)
			.filter((token) => !VAGUE_STEMS.has(token)),
	)
}

/**
 * True when a theme carries no subject at all, only a verdict.
 *
 * contentTokens already strips both stopwords and VAGUE_STEMS, so "has no
 * content tokens" is the whole definition. There is deliberately no second
 * check that the surviving tokens are vague: none can be, and such a check
 * would be unreachable.
 *
 *   "customer service"     -> {}         vague
 *   "quality of work"      -> {}         vague
 *   "septic service"       -> {septic}   not vague
 *   "junk removal service" -> {junk, removal}  not vague
 */
export function isVagueTheme(theme: string): boolean {
	return contentTokens(theme).size === 0
}

/**
 * Merges themes that share their topical vocabulary.
 *
 * This is deliberately category-agnostic. An earlier version hardcoded roofing
 * synonyms (storm, gutter, shingle, leak), which would have required writing a
 * vocabulary list for each of the 106 categories: the fine-tuning that does not
 * scale. Instead, overlap is computed from the themes themselves, so
 * "insurance claim assistance", "insurance assistance" and "insurance claim
 * support" collapse in roofing for the same reason that plumbing's own
 * variants will collapse, without anyone naming either domain.
 *
 * The surviving label is the shortest, which is reliably the most general
 * phrasing of the shared idea.
 */
export interface ThemeGroup {
	/** Surviving label: the shortest, most general phrasing in the group. */
	label: string
	/** Every input theme that collapsed into this group. */
	members: string[]
}

/**
 * Groups themes by shared topical vocabulary and returns the groups, so
 * callers can both use the canonical labels and map originals onto them.
 */
export function groupRelatedThemes(themes: string[]): ThemeGroup[] {
	/*
	 * Sorted for determinism. Greedy first-match grouping is order dependent,
	 * so an unsorted flatMap could produce different clusters on reruns over
	 * the same data. Fewest tokens first, then shortest, then alphabetical.
	 */
	const entries = themes
		.map((theme) => ({ theme, tokens: contentTokens(theme) }))
		.filter((entry) => entry.tokens.size > 0)
		.sort(
			(a, b) =>
				a.tokens.size - b.tokens.size ||
				a.theme.length - b.theme.length ||
				a.theme.localeCompare(b.theme),
		)

	const groups: Array<{
		label: string
		members: Array<{ theme: string; tokens: Set<string> }>
	}> = []

	/*
	 * Relatedness is judged against each existing MEMBER's own tokens, never
	 * against a growing union of the group's vocabulary.
	 *
	 * Unioning creates transitive glue: "insurance assistance" reduces to
	 * {insurance} after stopwords, absorbs "insurance claim paperwork", the
	 * group's vocabulary grows to include {claim, paperwork}, and then
	 * "paperwork delays" joins something it has nothing to do with. Hub words
	 * like repair and scheduling do the same.
	 */
	const isRelated = (
		a: { tokens: Set<string> },
		b: { tokens: Set<string> },
	): boolean => {
		const shared = [...a.tokens].filter((token) => b.tokens.has(token))
		if (shared.length === 0) return false

		// A single shared stem is only enough when one side is a single token,
		// i.e. it genuinely has nothing else to say.
		const bothMultiToken = a.tokens.size >= 2 && b.tokens.size >= 2
		if (bothMultiToken && shared.length < 2) return false

		const smaller = Math.min(a.tokens.size, b.tokens.size)
		return shared.length / smaller >= 0.5
	}

	for (const entry of entries) {
		const match = groups.find((group) =>
			group.members.some((member) => isRelated(entry, member)),
		)

		if (match) {
			match.members.push(entry)
			// Prefer the shorter label: the more general phrasing of the idea.
			if (entry.theme.length < match.label.length) match.label = entry.theme
		} else {
			groups.push({ label: entry.theme, members: [entry] })
		}
	}

	return groups.map(({ label, members }) => ({
		label,
		members: members.map((member) => member.theme),
	}))
}

/**
 * Maps every input theme onto the canonical label that absorbed it. This is
 * what callers need when rewriting stored themes onto a shared vocabulary.
 */
export function buildThemeCanonicalMap(themes: string[]): Map<string, string> {
	// Takes RAW themes, not normalized ones. Passing the output of
	// normalizeThemes here would be self-defeating: the variants have already
	// been collapsed, so the map would only ever point labels at themselves.
	const cleaned = [
		...new Set(
			themes
				.map((theme) => theme.trim().toLowerCase())
				.filter((theme) => theme && !isVagueTheme(theme)),
		),
	]

	const mapping = new Map<string, string>()
	for (const group of groupRelatedThemes(cleaned)) {
		for (const member of group.members) {
			mapping.set(member, group.label)
		}
	}
	return mapping
}

/**
 * Normalizes a raw theme list: drops verdict-words, then merges themes that
 * share their topical vocabulary. Returns lowercase noun phrases suitable for
 * grouping and filtering across businesses in the same category.
 */
export function normalizeThemes(themes: string[]): string[] {
	const seen = new Set<string>()

	for (const raw of themes) {
		const theme = raw.trim().toLowerCase()
		if (!theme) continue
		if (isVagueTheme(theme)) continue
		seen.add(theme)
	}

	return groupRelatedThemes([...seen]).map((group) => group.label)
}

export interface PersistedClaim {
	text: string
	/** Review ids supporting this claim, resolved from prompt indexes. */
	reviewIds: string[]
}

export interface PersistedClaims {
	pros: PersistedClaim[]
	watchOuts: PersistedClaim[]
	bestFor: PersistedClaim | null
}

/**
 * Resolves prompt indexes back to Review ids so citations outlive the request.
 *
 * Without this the gate is only a write-time filter: the evidence that made a
 * claim publishable is thrown away, so pages cannot show "supported by N
 * reviews" and nobody can audit a claim later. Storing ids makes the grounding
 * durable and is the prerequisite for surfacing evidence on /best/.
 */
export function resolveClaimCitations(
	// Only the cited fields, so this accepts both the raw model output and the
	// validated result (whose sentimentScore may be null).
	synthesis: Pick<
		ReviewSynthesis,
		'pros' | 'watchOuts' | 'bestFor' | 'bestForCitations'
	>,
	/** Review ids in the same order they were shown to the model. */
	promptReviewIds: string[],
): PersistedClaims {
	const toIds = (citations: number[]): string[] =>
		[...new Set(citations)]
			.map((index) => promptReviewIds[index])
			.filter((id): id is string => Boolean(id))

	return {
		pros: synthesis.pros.map((claim) => ({
			text: claim.text,
			reviewIds: toIds(claim.citations),
		})),
		watchOuts: synthesis.watchOuts.map((claim) => ({
			text: claim.text,
			reviewIds: toIds(claim.citations),
		})),
		bestFor: synthesis.bestFor
			? {
					text: synthesis.bestFor,
					reviewIds: toIds(synthesis.bestForCitations),
				}
			: null,
	}
}

/**
 * Stable fingerprint of the review corpus. When this is unchanged, synthesis
 * is skipped — LLM calls cost money and the output would be identical.
 */
export function buildSynthesisHash(
	reviewIds: string[],
	promptVersion: string,
): string {
	const sorted = [...reviewIds].sort().join('|')
	let hash = 0
	for (let i = 0; i < sorted.length; i++) {
		hash = (hash << 5) - hash + sorted.charCodeAt(i)
		hash |= 0
	}
	return `${promptVersion}:${reviewIds.length}:${hash.toString(36)}`
}

/** Bump when the prompt changes so existing syntheses regenerate. */
export const SYNTHESIS_PROMPT_VERSION = 'v7'

/**
 * What a business actually has to show for its review analysis, as opposed to
 * whether the analysis ran.
 *
 * `synthesisAt` alone cannot answer that. A run where every claim failed
 * grounding validation still stamps `synthesisAt` and `sentimentScore` — it
 * deliberately leaves the published editorial untouched rather than blanking it
 * — so a business with nothing written about it is indistinguishable from a
 * fully written one by that timestamp. It ranks on its score all the same,
 * because the score never depended on the synthesis.
 *
 * That is the state this exists to name. `no_claims` is a business that is
 * ranking with nothing written about it, and it is the one worth re-running.
 */
export type AnalysisState =
	/** Editorial is published and citable. */
	| 'published'
	/** Analysis ran, nothing survived validation. Re-running is free and may fix it. */
	| 'no_claims'
	/** Corpus is there, analysis has never run. */
	| 'never_run'
	/** Too few reviews to say anything. Re-running cannot help; collect reviews first. */
	| 'no_corpus'

export function classifyAnalysisState(business: {
	reviewsHeld: number | null
	synthesisAt: Date | string | null
	pros: string[]
	watchOuts: string[]
}): AnalysisState {
	if (!business.synthesisAt) {
		return (business.reviewsHeld ?? 0) >= MIN_REVIEWS_TO_SYNTHESIZE
			? 'never_run'
			: 'no_corpus'
	}

	/*
	 * The same test validateGrounding publishes on: a surviving claim of either
	 * kind, and nothing else. It has to be, or the dashboard and the public pages
	 * disagree about the same business.
	 *
	 * Watch-outs count. A synthesis that survives with cautions alone returns ok,
	 * stamps the corpus hash, and renders those cautions on /best/ and on the
	 * listing — calling that "published nothing" would flag a business whose
	 * editorial is live and send a reviewer to re-run a hash-current corpus.
	 *
	 * The ranking paragraph is deliberately not part of the test. It is gated on
	 * hasSurvivingClaims and can still be dropped on its own for style, so a
	 * business can publish cited claims with no paragraph. That is thinner
	 * editorial, not absent editorial, and it is not what a re-run fixes.
	 */
	if (business.pros.length === 0 && business.watchOuts.length === 0) {
		return 'no_claims'
	}

	return 'published'
}
